import React, { useState } from 'react'
import { Button, Grid, makeStyles, Typography } from '@material-ui/core'
import { SubjectStatus, SubjectTypesList } from 'constants/index'
import { Box } from '@mui/material'
import { resetSubject, sendInviteEmailToSpecificSubject, updateSubjectById } from 'store/subject'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { subjectUpdateSchema } from 'schemas/subjects'
import { Form, Formik } from 'formik'
import CustomSelect from 'components/Form/CustomSelect'
import ProgressButton from 'components/ProgressButton'
import CustomTextField from 'components/Form/CustomTextField'
import { nanoid } from 'nanoid'
import ConfirmModal from 'components/ConfirmationModal'

const UpdateSubjectForm = ({ subjectId }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const [openInviteModal, setOpenInviteModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)

  const { subject } = useSelector((state) => state.subjects)
  const { conference } = useSelector((state) => state.conference)

  const initialValues = {
    type: subject?.type ?? '',
    email: subject?.email ?? ''
  }

  const goBack = () => {
    dispatch(resetSubject())
    history.goBack()
  }

  const handleOpenInviteModal = () => {
    setOpenInviteModal(true)
  }
  const handleCloseInviteModal = () => {
    setOpenInviteModal(false)
    goBack()
  }

  const handleConfirm = async () => {
    setInviteLoading(true)
    const payload = { status: SubjectStatus.AWAITING_RESPONSE }
    await sendInviteEmailToSpecificSubject(subjectId, subject?.conferenceId)
    await dispatch(
      updateSubjectById({ showSuccessMsg: false }, subjectId, payload, subject?.conferenceId)
    )
    setInviteLoading(false)
  }

  const onSubmit = async ({ type, email }) => {
    setLoading(true)
    const payload = {
      type,
      email,
      conferenceId: subject?.conferenceId
    }
    if (
      subjectId &&
      subject.id === subjectId &&
      subject.status === SubjectStatus.AWAITING_RESPONSE
    ) {
      const updatedPayload = {
        ...payload,
        status: SubjectStatus.INVITE_NOT_SENT,
        token: nanoid()
      }
      const isSubjectUpdated = await dispatch(
        updateSubjectById(
          { showSuccessMsg: true },
          subjectId,
          updatedPayload,
          subject?.conferenceId
        )
      )
      isSubjectUpdated && handleOpenInviteModal()
    } else {
      const isUpdated = await dispatch(
        updateSubjectById({ showSuccessMsg: true }, subjectId, payload, subject?.conferenceId)
      )
      isUpdated && goBack()
    }
    setLoading(false)
  }
  return (
    <>
      <ConfirmModal
        dialogTitle="Send Invite Email"
        dialogText="The recipient's information has been updated, causing the previous email link to expire. Please send a new invitation with the updated details."
        open={openInviteModal}
        closeModal={handleCloseInviteModal}
        inProgress={inviteLoading}
        handleConfirm={handleConfirm}
      />
      <Box className={classes.headBar}>
        <Typography variant="h4">Update Subject</Typography>
        <Button color="primary" variant="contained" onClick={goBack}>
          Back
        </Button>
      </Box>

      <Box p={1} border={1} sx={{ mb: '2rem' }}>
        <Box
          p={1}
          sx={{
            backgroundColor: '#ECECEC',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
          <Box className={classes.singleInfo}>
            <Typography className={classes.label}>Conference Name:</Typography>
            <Typography>{conference?.confName}</Typography>
          </Box>
        </Box>
      </Box>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={subjectUpdateSchema}
        onSubmit={onSubmit}>
        {() => (
          <Form autoComplete="off" style={{ marginBottom: '1rem', marginTop: '1rem' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <CustomSelect
                  showLabel={true}
                  inputLabel="Subject Type"
                  options={SubjectTypesList}
                  optionAttr="type"
                  name="type"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField
                  label="Subject Email"
                  name="email"
                  type="text"
                  autoComplete="off"
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <Box className={classes.buttonBox}>
                  <Button variant="contained" size="large" disabled={loading} onClick={goBack}>
                    Cancel
                  </Button>
                  <ProgressButton
                    type="submit"
                    inProgress={loading}
                    label="Update"
                    disabled={loading}
                  />
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default UpdateSubjectForm

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0rem 0rem 2rem 0rem'
  },
  buttonBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '0.75rem'
  },
  singleInfo: {
    display: 'flex',
    gap: '2rem'
  },
  label: {
    minWidth: '132px',
    fontSize: '15px',
    fontWeight: '600'
  }
})
